/**
 * React Query hook for searching RxNorm medication database.
 *
 * Debounced search that queries the NIH RxNorm REST API.
 * Falls back to approximate (fuzzy) search if exact match returns no results.
 */

import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  searchDrugs,
  approximateSearch,
  fetchProperties,
  parseDrugName,
} from "../rxnormApi";
import type { RxNormSearchResult } from "../types";

// ============================================================================
// Query Keys
// ============================================================================

export const rxnormKeys = {
  all: ["rxnorm"] as const,
  search: (term: string) => [...rxnormKeys.all, "search", term] as const,
  properties: (rxcui: string) =>
    [...rxnormKeys.all, "properties", rxcui] as const,
};

// ============================================================================
// Debounce Hook
// ============================================================================

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

// ============================================================================
// Search Hook
// ============================================================================

/**
 * Search RxNorm for medications by name.
 *
 * - Debounces input by 300ms
 * - Tries exact search first, falls back to approximate/fuzzy
 * - Deduplicates by rxcui
 * - Returns max 10 results
 */
export function useRxNormSearch(query: string) {
  const debouncedQuery = useDebouncedValue(query.trim(), 300);

  return useQuery({
    queryKey: rxnormKeys.search(debouncedQuery),
    queryFn: async (): Promise<RxNormSearchResult[]> => {
      // Try exact search first
      let results = await searchDrugs(debouncedQuery);

      // Fall back to approximate search if no results
      if (results.length === 0) {
        results = await approximateSearch(debouncedQuery, 10);
      }

      // Deduplicate by rxcui
      const seen = new Set<string>();
      return results
        .filter((r) => {
          if (seen.has(r.rxcui)) return false;
          seen.add(r.rxcui);
          return true;
        })
        .slice(0, 10);
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    retry: 1,
  });
}

// ============================================================================
// Properties Hook
// ============================================================================

/**
 * Fetch detailed properties for a selected RxNorm medication.
 * Call after user selects a result to get auto-fill data.
 */
export function useRxNormProperties(rxcui: string | null) {
  return useQuery({
    queryKey: rxnormKeys.properties(rxcui ?? ""),
    queryFn: async () => {
      if (!rxcui) return null;
      return fetchProperties(rxcui);
    },
    enabled: !!rxcui,
    staleTime: 1000 * 60 * 30, // 30 minutes — drug properties don't change
    gcTime: 1000 * 60 * 60, // 1 hour
  });
}

// Re-export parser for use in components
export { parseDrugName };
